import { Backdrop, Card } from '@material-ui/core';
import React, { useMemo, useState } from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Modal from '@/src/components/common/Elements/Modal/Modal';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { FileEntryViewerInfoPanelProvider } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/store/fileEntryViewer.infoPanel.store';
import { FileEntryViewerProvider } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/store/fileEntryViewer.store';
import { InboxProvider } from '@/src/components/project/Inbox2/store/inbox.store';
import { cn } from '@/src/util/ui';

import { useGetLibraryItemDetails } from './api/getLibraryItemDetails';
import { FileEntryViewer } from './FileEntryViewer';
import { useInfoPanelQueries } from './useInfoPanelQueries';

import { useFileList } from '../../../store/file-list.store';

export const FileEntryViewerWrapper = ({
  file,
  fileEntry,
  onClose,
  hasPrev,
  hasNext,
  onNext,
  onPrev,
}) => {
  const showNavIcons = hasPrev || hasNext;

  return (
    <ModalPortal>
      <Modal id={'library-fileviewer'} className={cn('!tw-z-10')}>
        <Backdrop open={true} onClick={onClose}>
          <Card
            className={cn(
              'modal-dialog',
              'tw-w-full tw-max-w-[95vw] tw-mx-auto test class',
              'tw-h-screen tw-max-h-[90vh]'
            )}
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'none' }}
          >
            <div className="tw-flex tw-items-stretch tw-h-full">
              {showNavIcons && (
                <div className="tw-flex tw-items-center tw-justify-center tw-w-[100px]">
                  <Button
                    className={cn(
                      'arrow-prev tw-rounded-[100px] tw-text-white tw-bg-[#FFFFFF80]',
                      'tw-h-[54px] tw-w-[54px] tw-m-0',
                      !hasPrev && 'tw-pointer-events-none'
                    )}
                    onClick={() => hasPrev && onPrev()}
                  >
                    <Icon icon={ICONS.arrow_left} />
                  </Button>
                </div>
              )}

              <FileEntryViewer file={file} onClose={onClose} fileEntry={fileEntry} />
              {showNavIcons && (
                <div className="tw-flex tw-items-center tw-justify-center tw-w-[100px]">
                  <Button
                    className={cn(
                      'arrow-next tw-rounded-[100px] tw-text-white tw-bg-[#FFFFFF80]',
                      'tw-h-[54px] tw-w-[54px] tw-m-0',
                      !hasNext && 'tw-pointer-events-none'
                    )}
                    onClick={() => hasNext && onNext()}
                  >
                    <Icon icon={ICONS.arrow_right} />
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </Backdrop>
      </Modal>
    </ModalPortal>
  );
};

export const LibraryFileEntryViewer = ({ file, onClose }) => {
  const { filesQuery } = useFileList();
  const flatFiles = useMemo(
    () =>
      filesQuery.data?.pages.flatMap((page) => page.data).filter((f) => f.source === 'document') ||
      [],
    [filesQuery.data]
  );

  const [sourceId, setSourceId] = useState(file['id']);
  const [currentIndex, setCurrrentIndex] = useState(flatFiles.findIndex((f) => f.id === file.id));

  const libraryItemDetailsQuery = useGetLibraryItemDetails({
    libraryItemId: sourceId,
  });
  const fileEntry = libraryItemDetailsQuery.data?.data;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < flatFiles.length - 1;
  const handleNext = () => {
    const newSelectedFile = flatFiles[currentIndex + 1];

    setCurrrentIndex((prev) => prev + 1);
    setSourceId(newSelectedFile['id']);
  };
  const handlePrev = () => {
    const newSelectedFile = flatFiles[currentIndex - 1];

    setCurrrentIndex((prev) => prev - 1);
    setSourceId(newSelectedFile['id']);
  };

  const selectedFile = flatFiles[currentIndex];

  const infoPanelQueries = useInfoPanelQueries({
    libraryItemId: file.id,
    sourceId,
  });

  if (!fileEntry) {
    return <Spinner />;
  }

  return (
    <InboxProvider isTreatmentHidden={true}>
      <FileEntryViewerInfoPanelProvider infoPanelQueries={infoPanelQueries}>
        <FileEntryViewerProvider>
          <FileEntryViewerWrapper
            file={selectedFile}
            onClose={onClose}
            fileEntry={fileEntry}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        </FileEntryViewerProvider>
      </FileEntryViewerInfoPanelProvider>
    </InboxProvider>
  );
};
